import { ElectionElectoratePartRecord } from '@/apis/elections';
import { InvalidSearchTreeError } from '@/errors/InvalidSearchTreeError';
import {
  arePermissibleTaskChoicesAndKinds,
  CallingStatusFilter,
  EngagementHistoryFilter,
  EventAttendanceFilter,
  isTemplatePlaceholder,
  SearchTemplate,
  SearchTreeNode,
  TaskFilter,
} from '@/types';
import { labelSorting } from '@/util/labelSorting';
import { FieldMetaData } from './templateFormControls/types';

export function parseSearchTemplateTree(searchTemplate: SearchTemplate) {
  try {
    // TODO: Validate tree?
    const searchTemplateTree = JSON.parse(searchTemplate.searchTemplateJson);
    if (
      !searchTemplateTree ||
      typeof searchTemplateTree !== 'object' ||
      Array.isArray(searchTemplateTree)
    ) {
      throw new InvalidSearchTreeError('Invalid search template tree structure');
    }
    return searchTemplateTree as SearchTreeNode;
  } catch (err) {
    if (err instanceof InvalidSearchTreeError) {
      throw err;
    } else {
      throw new Error('Failed to parse search template tree', { cause: err });
    }
  }
}

type DiscriminatedFilterAndName =
  | {
      filterName: 'callingStatus';
      filter: CallingStatusFilter;
    }
  | {
      filterName: 'engagementHistory';
      filter: EngagementHistoryFilter;
    }
  | {
      filterName: 'eventAttendance';
      filter: EventAttendanceFilter;
    };

const makeSetFieldToInvalidInMetaDataFunction =
  (metaDataOfPlaceholderFields: Record<string, FieldMetaData>, reason: string) =>
  (key: string, helperText: string) => {
    metaDataOfPlaceholderFields[key] = {
      state: 'invalid',
      error: {
        reason,
        helperText,
      },
    };
  };

/**
 * Updates the entries in metaDataOfPlaceholderFields (IN PLACE) according to the date
 * range validation of the two dates when considered together. This includes
 * resetting one back to valid from 'partOfInvalidDateRange' if the other
 * changes
 */
export function validateDateRangesAndUpdateMetaData(
  { filter, filterName }: DiscriminatedFilterAndName,
  metaDataOfPlaceholderFields: Record<string, FieldMetaData>,
  nodeId: string,
  hashFn: (nodeId: string, filterNameAndField: string) => string
) {
  let startDate, endDate, startDateKey, endDateKey;
  if (filterName === 'callingStatus') {
    startDate = filter.value.callingStatusDateRange.displayedStartDate;
    endDate = filter.value.callingStatusDateRange.displayedEndDate;
    startDateKey = hashFn(nodeId, 'callingStatus.callingStatusDateRange.displayedStartDate');
    endDateKey = hashFn(nodeId, 'callingStatus.callingStatusDateRange.displayedEndDate');
  } else if (filterName === 'engagementHistory') {
    startDate = filter.value.activityDateRange.displayedStartDate;
    endDate = filter.value.activityDateRange.displayedEndDate;
    startDateKey = hashFn(nodeId, 'engagementHistory.activityDateRange.displayedStartDate');
    endDateKey = hashFn(nodeId, 'engagementHistory.activityDateRange.displayedEndDate');
  } else {
    startDate = filter.value.eventDateRange.startDate;
    endDate = filter.value.eventDateRange.endDate;
    startDateKey = hashFn(nodeId, 'eventAttendance.eventDateRange.startDate');
    endDateKey = hashFn(nodeId, 'eventAttendance.eventDateRange.endDate');
  }

  // If either one is still a placeholder, no validation can be done, user needs
  // to fill more out (its never possible for it to go back to a placeholder)
  if (isTemplatePlaceholder(startDate) || isTemplatePlaceholder(endDate)) {
    return;
  }

  const startDateHasMetaData = startDateKey in metaDataOfPlaceholderFields;
  const endDateHasMetaData = endDateKey in metaDataOfPlaceholderFields;

  const resetToValid = (key: string) => {
    const metaData = metaDataOfPlaceholderFields[key];
    // Only reset if it was part of an invalid combination
    if (metaData?.error?.reason === 'partOfInvalidDateRange') {
      metaDataOfPlaceholderFields[key] = {
        state: 'valid',
        error: null,
      };
    }
  };

  const setToInvalid = makeSetFieldToInvalidInMetaDataFunction(
    metaDataOfPlaceholderFields,
    'partOfInvalidDateRange'
  );

  // We could look at the metadata state to check validity, but we know the
  // values are set to null if not valid anyway and this simplifies the code

  // These are ISO8601 dates so we can rely on lexographical ordering
  if (startDate && endDate && startDate > endDate) {
    // Having meta data means they are visible in the dialog
    if (startDateHasMetaData) {
      setToInvalid(startDateKey, 'Start date must be before end date');
    }
    if (endDateHasMetaData) {
      setToInvalid(endDateKey, 'End date must be after start date');
    }
  } else {
    // Reset any previous range validation
    resetToValid(startDateKey);
    resetToValid(endDateKey);
  }

  // There are no other combinations we need to worry about
}

/**
 * Updates the entries in metaDataOfPlaceholderFields (IN PLACE) according to
 * combination of task kinds and task choices. This includes resetting one back
 * to valid from 'partOfInvalidTaskChoiceAndKindCombination' if the other changes
 */
export function validateTaskAndUpdateMetaData(
  filter: TaskFilter,
  metaDataOfPlaceholderFields: Record<string, FieldMetaData>,
  nodeId: string,
  hashFn: (nodeId: string, filterNameAndField: string) => string
) {
  const { kinds, choices } = filter.value;

  if (isTemplatePlaceholder(kinds) || isTemplatePlaceholder(choices)) {
    return;
  }

  const kindsKey = hashFn(nodeId, 'task.kinds');
  const choicesKey = hashFn(nodeId, 'task.choices');

  const kindsHasMetaData = kindsKey in metaDataOfPlaceholderFields;
  const choicesHasMetaData = choicesKey in metaDataOfPlaceholderFields;

  const resetToValid = (key: string) => {
    const metaData = metaDataOfPlaceholderFields[key];
    // Only reset if it was part of an invalid combination
    if (metaData?.error?.reason === 'partOfInvalidTaskChoiceAndKindCombination') {
      metaDataOfPlaceholderFields[key] = {
        state: 'valid',
        error: null,
      };
    }
  };

  const setToInvalid = makeSetFieldToInvalidInMetaDataFunction(
    metaDataOfPlaceholderFields,
    'partOfInvalidTaskChoiceAndKindCombination'
  );

  if (!arePermissibleTaskChoicesAndKinds(choices, kinds)) {
    // Having meta data means they are visible in the dialog
    if (kindsHasMetaData && choicesHasMetaData) {
      // Both are visible - we only want to display message under the status,
      // so set the message to blank for choices (aka kinds)
      // setToInvalid(choicesKey, 'Task status invalid for selected task type');
      setToInvalid(choicesKey, 'Task status invalid for selected task type');
      setToInvalid(kindsKey, '');
    } else if (kindsHasMetaData) {
      setToInvalid(kindsKey, 'Task type invalid for selected task status');
    } else if (choicesHasMetaData) {
      setToInvalid(choicesKey, 'Task status invalid for selected task type');
    }
  } else {
    if (kindsHasMetaData) resetToValid(kindsKey);
    if (choicesHasMetaData) resetToValid(choicesKey);
  }
}

export function mapElectoratesForAutocomplete(electorates: ElectionElectoratePartRecord[]) {
  return electorates
    .map((x) => {
      return {
        id: x.id,
        label: x.name,
      };
    })
    .sort(labelSorting);
}
